import type { AnimTrack, Asset, Entity } from 'playcanvas';

import type { AvatarAnimationMapping, WalkAvatarState } from './AvatarTypes';

// 动画 crossfade 时长（秒）。参考 hunter-world 0.1，这里 0.18 略柔和，避免过急
const ANIMATION_BLEND_TIME = 0.18;
const MOVE_EPSILON = 0.005;
// 奔跑(sprint)滞回阈值：进入/退出分离，避免在边界速度抖动时 run/sprint 反复切换。
// WalkController 稳态水平速度约为：普通走 2.6 m/s、按住 Shift 约 5.1 m/s。
const SPRINT_ENTER_SPEED = 4.2;
const SPRINT_EXIT_SPEED = 3.2;
const ANIM_FALL_DELAY = 0.12;

/** Avatar 动画控制器，负责根据行走状态切换 GLB 内置动画。 */
class AvatarAnimationController {
    private _entity: Entity;

    private _mapping: AvatarAnimationMapping;

    private _animationNames: string[];

    private _tracksByName = new Map<string, AnimTrack>();

    private _current = '';

    private _airTime = 0;

    private _wasJumping = false;

    private _jumpHadInput = false;

    /** 奔跑滞回状态，配合进入/退出阈值稳定 run/sprint 切换。 */
    private _sprinting = false;

    private _missingAnimationOnce = new Set<string>();

    private _debug: boolean;

    constructor(entity: Entity, animations: Asset[], mapping: AvatarAnimationMapping, debug = false) {
        this._entity = entity;
        this._mapping = { ...mapping };
        this._animationNames = [];
        this._debug = debug;

        for (const animation of animations) {
            const track = animation.resource as AnimTrack | null;
            if (!track) {
                continue;
            }
            const trackName = track.name || animation.name;
            this._tracksByName.set(trackName, track);
            this._animationNames.push(trackName);
        }

        this._entity.addComponent('anim', {
            activate: true,
            speed: 1
        });

        const states = [
            { name: 'idle', speed: 1, loop: true, defaultState: true },
            { name: 'run', speed: 1, loop: true },
            { name: 'sprint', speed: 1, loop: true },
            { name: 'jumpIdle', speed: 1, loop: false },
            { name: 'jumpRun', speed: 1, loop: false },
            { name: 'fall', speed: 1, loop: true },
            { name: 'rotateLeft', speed: 1, loop: true },
            { name: 'rotateRight', speed: 1, loop: true }
        ];

        // 用 ANY_STATE 通配为每个语义状态预定义带 blend time 的 transition 路径，
        // 这样 layer.transition(name) 即可触发统一时长的 weight crossfade，
        // 重复触发时也会被 PlayCanvas 内部按 ANY_STATE 规则收敛，避免硬切/闪烁。
        const anyStateTransitions = states.map(s => ({
            from: 'ANY_STATE',
            to: s.name,
            time: ANIMATION_BLEND_TIME
        }));

        this._entity.anim.loadStateGraph({
            layers: [{
                name: 'Base',
                states: [
                    { name: 'START', speed: 1 },
                    ...states
                ],
                transitions: [
                    { from: 'START', to: 'idle' },
                    ...anyStateTransitions
                ]
            }],
            parameters: {}
        });

        this._assignSemanticAnimations();

        this._log('动画控制器初始化完成', {
            animationCount: this._animationNames.length,
            mapping: this._mapping,
            animations: this._animationNames
        });

        this.play('idle');
    }

    get animationNames(): string[] {
        return [...this._animationNames];
    }

    get current(): string {
        return this._current;
    }

    /**
     * 更新动画语义映射。
     *
     * @param mapping - 新的动画语义映射。
     */
    setMapping(mapping: AvatarAnimationMapping): void {
        this._mapping = { ...mapping };
        this._assignSemanticAnimations();
    }

    /**
     * 根据当前漫游状态选择并播放最合适的动画。
     *
     * @param state - 当前行走控制器状态。
     */
    update(state: WalkAvatarState): void {
        this._updateAirState(state);
        const target = this._selectAnimation(state);
        this.play(target);
    }

    /**
     * 播放指定动画，缺失时自动降级到 idle 或首个动画。
     *
     * @param name - 需要播放的动画名称。
     */
    play(name: string): void {
        if (!name || name === this._current) {
            return;
        }

        try {
            const prev = this._current;
            const layer = this._entity.anim.baseLayer;
            // 首次播放（_current 为空）直接 play，避免从 START 状态跳变；
            // 之后用 transition(to, time) 在两个状态间做权重 crossfade（参考 hunter-world weight 0→1 / 1→0）
            if (!prev) {
                layer.play(name);
            } else {
                layer.transition(name, ANIMATION_BLEND_TIME);
            }
            this._current = name;
            this._log('动画切换', { from: prev || '(none)', to: name, blend: prev ? ANIMATION_BLEND_TIME : 0 });
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.warn(`[AvatarAnimationController] 播放动画失败: ${name} - ${message}`);
        }
    }

    private _selectAnimation(state: WalkAvatarState): string {
        const horizontalSpeed = Math.hypot(state.velocity.x, state.velocity.z);
        const moving = state.moveMagnitude > MOVE_EPSILON;

        if (!state.grounded) {
            this._sprinting = false;
            if (this._wasJumping && state.velocity.y >= -0.05) {
                return this._jumpHadInput ? 'jumpRun' : 'jumpIdle';
            }
            if (this._airTime > ANIM_FALL_DELAY) {
                return 'fall';
            }
            return this._jumpHadInput || moving ? 'jumpRun' : 'jumpIdle';
        }

        if (moving) {
            // 带滞回的 run/sprint 选择：高于进入阈值才切 sprint，低于退出阈值才退回 run
            if (this._sprinting) {
                if (horizontalSpeed < SPRINT_EXIT_SPEED) {
                    this._sprinting = false;
                }
            } else if (horizontalSpeed >= SPRINT_ENTER_SPEED) {
                this._sprinting = true;
            }
            return this._sprinting ? 'sprint' : 'run';
        }

        this._sprinting = false;
        return 'idle';
    }

    private _assignSemanticAnimations(): void {
        const semanticMapping: Record<string, string> = {
            idle: this._mapping.idle,
            run: this._mapping.run,
            sprint: this._mapping.sprint,
            jumpIdle: this._mapping.jumpIdle,
            jumpRun: this._mapping.jumpRun,
            fall: this._mapping.fall,
            rotateLeft: this._mapping.rotateLeft,
            rotateRight: this._mapping.rotateRight
        };

        for (const [stateName, animationName] of Object.entries(semanticMapping)) {
            const resolved = this._resolve(animationName);
            const track = this._tracksByName.get(resolved);
            if (track) {
                this._entity.anim.assignAnimation(stateName, track);
                this._log('语义动画绑定', { state: stateName, requested: animationName, resolved });
            } else if (!this._missingAnimationOnce.has(stateName)) {
                this._missingAnimationOnce.add(stateName);
                console.warn(`[AvatarAnimationController] 语义动画未绑定: ${stateName} -> ${animationName}`);
            }
        }
    }

    private _log(message: string, data?: object): void {
        if (!this._debug) {
            return;
        }
        if (data) {
            console.log(`[AvatarAnimationController] ${message}`, data);
        } else {
            console.log(`[AvatarAnimationController] ${message}`);
        }
    }

    private _updateAirState(state: WalkAvatarState): void {
        if (state.jumpStarted) {
            this._wasJumping = true;
            this._jumpHadInput = state.jumpHadInput;
            this._airTime = 0;
        }

        if (!state.grounded) {
            this._airTime += 1 / 60;
            if (this._wasJumping && state.velocity.y < 0) {
                this._wasJumping = false;
            }
        } else {
            this._airTime = 0;
            this._wasJumping = false;
            this._jumpHadInput = false;
        }
    }

    private _resolve(name: string): string {
        if (this._animationNames.includes(name)) {
            return name;
        }

        const lower = name.toLowerCase();
        const fuzzy = this._animationNames.find(item => item.toLowerCase() === lower) ??
            this._animationNames.find(item => item.toLowerCase().includes(lower));

        return fuzzy ?? this._animationNames.find(item => item === this._mapping.idle) ?? this._animationNames[0] ?? '';
    }
}

export { AvatarAnimationController };
